import {
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { AtticusForm } from "../../../models/AtticusForm";
import { PdfFileChoice, useAtticusFormViewer } from "./useAtticusFormViewer";
import circleThumbnailBackdrop from "./../../assets/circle-thumbnail-backdrop-1x.png";
import genericFormDraftThumbnail from "../../assets/generic-form-draft-thumbnail-1x.png";
import { useContext, useEffect, useState } from "react";
import { logger } from "apis";
import { EstateContext } from "EstateContextWrapper";

interface Props {
  open: boolean;
  onClose: () => void;
  form: AtticusForm;
  estateId: number;
}

export default function AtticusFormConfirmRemoveDialog({
  open,
  onClose,
  form,
  estateId,
}: Props): JSX.Element {
  if (!open) {
    return <></>;
  }
  const { removeEstateForm } = useContext(EstateContext);
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  // default to generic thumbnail fallback
  const [thumbnailUrlToUse, setThumbnailUrlToUse] = useState<
    string | undefined
  >(genericFormDraftThumbnail);
  const [failedMessage, setFailedMessage] = useState<string | undefined>();
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const { fileUrl: thumbnailFileUrl } = useAtticusFormViewer({
    form,
    fileChoice: PdfFileChoice.THUMBNAIL,
  });

  useEffect(() => {
    // default to watermark but fallback to thumbnail or generic if not available for any reason
    if (thumbnailFileUrl) {
      (async () => {
        try {
          await fetch(thumbnailFileUrl);
          setThumbnailUrlToUse(thumbnailFileUrl);
        } catch (e) {
          logger.error(
            new Error("Failed to get form thumbnail for removal dialog, e=" + e)
          );
        }
      })();
    }
  }, [thumbnailFileUrl]);

  const handleClose = () => {
    setFailedMessage(undefined);
    onClose();
  };

  const handleRemove = async () => {
    try {
      setIsRemoving(true);
      await removeEstateForm(estateId, form);
      handleClose();
    } catch (e) {
      const err = new Error("Failed to remove form from my forms, e=" + e);
      err.stack = (e as Error)?.stack;
      logger.error(err);
      setFailedMessage("Unable to remove form");
    } finally {
      setIsRemoving(false);
    }
  };
  return (
    <Dialog
      fullScreen={isMobileSize}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          alignContent: "center",
          padding: 5,
        },
      }}
    >
      {open && form ? (
        <>
          {thumbnailUrlToUse && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    sx={{
                      height: 160,
                      width: 160,
                      objectFit: "contain",
                    }}
                    src={circleThumbnailBackdrop}
                  />
                  <Box
                    component="img"
                    sx={{
                      height: 120,
                      width: 92,
                      objectFit: "contain",
                      position: "absolute",
                      top: 160 / 2 - 120 / 2,
                      left: 160 / 2 - 92 / 2,
                    }}
                    src={thumbnailUrlToUse ?? genericFormDraftThumbnail}
                  />
                </Box>
              </Box>
            </>
          )}
          <DialogTitle id="responsive-dialog-title" component="div">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  maxWidth: 600,
                  alignSelf: "center",
                  textAlign: "center",
                }}
              >
                Please confirm this change
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  maxWidth: 700,
                  alignSelf: "center",
                  textAlign: "center",
                  marginTop: 3,
                }}
              >
                Making this change will remove <em>{form?.formTitle}</em> from
                being saved within your Forms. This action can be changed later
                by adding this form again from within the Atticus Forms Library.
              </Typography>

              {failedMessage ? (
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: 700,
                    alignSelf: "center",
                    textAlign: "center",
                    marginTop: 3,
                    color: "crimson",
                  }}
                >
                  {failedMessage}
                </Typography>
              ) : null}

              <Box
                sx={{
                  marginTop: 4,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    width: 275,
                  }}
                  onClick={() => handleClose()}
                  disabled={isRemoving}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    width: 275,
                  }}
                  onClick={() => {
                    handleRemove();
                  }}
                  disabled={isRemoving}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          </DialogTitle>
        </>
      ) : (
        <DialogContent
          sx={{
            textAlign: "center",
          }}
        >
          No form selected
        </DialogContent>
      )}
    </Dialog>
  );
}
